import { useEffect, useMemo, useState } from 'react';

import { min, max } from 'lodash-es';

import { cropsToRulerAllocations } from '../helpers/allocationsFunctions';
import { ImageToRulerLengthAllocations } from '../helpers/image-to-ruler-allocation';
import { ImageTypeMetadataCrop } from '../types/imageTypeMetadata';
import { LengthRange } from '../types/lengthRange';

/**
 * Represents properties for computing common alignment within a CoreViewer instance
 *
 * @property imageToRulerLengthAllocations - Defines the length allocations across all selected image types. If they are commonly aligned, this can include crops and gaps,
 * but otherwise it is a contiguous ruler from the shallowest to deepest image ranges.
 * @property commonAlignmentMarker - The marker shared by all selected image types.
 * If a string, the images are aligned, and can be displayed against this alignment marker
 * If false, the images should be aligned downhole (no gaps/crops).
 * If undefined, the alignment has not been calculated
 */
export interface UseCommonAlignedImageTypesState {
	imageToRulerLengthAllocations?: ImageToRulerLengthAllocations;
	commonAlignmentMarker: string | false | undefined;
}

/**
 * Represents data mandatory for an alignable display item
 */
export interface AlignmentMetadata {
	marker?: string;
	crops?: ImageTypeMetadataCrop[];
	range:
		| (LengthRange & {
				trayTo?: number;
		  })
		| undefined;
}

/**
 * A function to convert a generic ID for an alignable display type, to a unique key and corresponding alignment metadata
 */
export type AlignmentItemMetadataProviderFn<TIdentifier> = (id: TIdentifier) => KeyedAlignmentItemMetadata | undefined;

export type KeyedAlignmentItemMetadata = { key: string } & AlignmentMetadata;

/**
 * Determines if a common alignment exists among selected image types based on their metadata
 * and computes alignment-related properties.
 *
 * @param {number[]} alignedItemIds - The IDs of the selected alignable types to analyze. They are generic,
 * as we may have locally unique ID values depending on the data type being displayed
 * @param {ImageTypeLoaderProps} alignmentItemProvider - A function for resolving the metadata, and a unique key for an identifier
 * @returns {UseCommonAlignedImageTypesState} An object containing the following properties:
 */
export const useCommonAlignedItems = <TIdentifier>(
	alignedItemIds: TIdentifier[],
	alignmentItemProvider: AlignmentItemMetadataProviderFn<TIdentifier>,
): UseCommonAlignedImageTypesState => {
	const [imageToRulerLengthAllocations, setImageToRulerLengthAllocations] = useState<ImageToRulerLengthAllocations>();
	const [commonAlignmentMarker, setCommonAlignmentMarker] = useState<string | false>();

	// Compute selectedMetadata once and reuse throughout the hook - eliminates double computation
	const selectedMetadata = useMemo(
		() =>
			alignedItemIds
				.map((id) => alignmentItemProvider(id))
				.filter((a): a is KeyedAlignmentItemMetadata => a !== undefined),
		[alignedItemIds, alignmentItemProvider],
	);

	/**
	 * The default allocation is a contiguous 1:1 image-to-ruler range spanning all selected items.
	 * Used as a fallback when crops are unavailable or alignment markers don't match.
	 */
	const downholeOnlyAllocation: ImageToRulerLengthAllocations = useMemo(() => {
		const fullRange = {
			depthFrom: selectedMetadata.length
				? (min(selectedMetadata.map((m) => m.range?.depthFrom).filter((d) => d !== undefined)) ?? 0)
				: 0,
			depthTo: selectedMetadata.length
				? (max(selectedMetadata.map((m) => m.range?.depthTo).filter((d) => d !== undefined)) ?? 0)
				: 0,
		};
		return [{ renderableDepth: fullRange, rulerDepth: fullRange }];
	}, [selectedMetadata]);

	/**
	 * When the selection changes, and/or image loaders update, then calculate the allocation
	 */
	useEffect(() => {
		/**
		 * For more than one image type, if any of the metadata has no crops, then assume that there is no uncropped file available. Thus, fall back to cropped images for all
		 * If there is no "range" property, then skip consideration of the crops for this item
		 */
		if (!selectedMetadata.reduce((prev, curr) => prev && (curr.range ? Boolean(curr.crops?.length) : true), true)) {
			setImageToRulerLengthAllocations(downholeOnlyAllocation);
			setCommonAlignmentMarker(false);
			return;
		}

		// Gets all unique alignment markers for metadata
		const alignments = [...new Set(selectedMetadata.map((m) => m.marker))];

		// No items, so we have no images to load! Set the marker as `undefined`,
		// and no images, or data will be loaded by the viewer
		if (!alignments.length) {
			setCommonAlignmentMarker(undefined);
			setImageToRulerLengthAllocations(undefined);
		} // We have more than one alignment, so set the marker as `false`
		else if (alignments.length > 1) {
			setCommonAlignmentMarker(false);
			setImageToRulerLengthAllocations(downholeOnlyAllocation);
		} // We have a single alignment, so use its marker and pull the allocations from an image
		else {
			const validMarker = alignments.find((a): a is string => typeof a === 'string' && a.length > 0);
			setCommonAlignmentMarker(validMarker ?? false);
			setImageToRulerLengthAllocations(cropsToRulerAllocations(selectedMetadata[0]) ?? downholeOnlyAllocation);
		}
	}, [downholeOnlyAllocation, selectedMetadata]);

	return {
		imageToRulerLengthAllocations,
		commonAlignmentMarker,
	};
};
